/**
 * The meeting form.
 *
 * **Attendees are two clearly separated groups, internal and customer — never
 * a single blended field.** `last_customer_contact_at` is computed from
 * meetings that have at least one `customer` attendee (§5.8), and `partner`
 * does not count: a blended field would let a partner-only or internal-only
 * meeting reset the "no contact in 30 days" clock, which is one of the seven
 * stall signals the product is built around.
 *
 * The **next steps section creates action items directly from the form**.
 * Capturing commitments at the moment of the meeting is the difference between
 * a system that reflects reality and one that does not.
 */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { MEETING_TYPES, SENTIMENTS, type LogMeetingInput } from '@menlo/shared';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { api, ApiError } from '@/lib/api';
import { useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { useToast } from '@/lib/toast';
import { addDays, fromDateTimeLocal, humanize, toDateTimeLocal, today } from '@/lib/format';
import { ACTIONS_QUERY_KEY } from '@/features/actions/ActionItems';

interface NextStep {
  title: string;
  ownerUserId: string;
  ownerContactId: string;
  dueDate: string;
}

export function MeetingDialog({
  accountId,
  opportunityId,
  meetingId,
  onClose,
  onSaved,
}: {
  accountId: string;
  opportunityId?: string | null;
  meetingId?: string;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const reference = useReferenceData();
  const { push } = useToast();
  const queryClient = useQueryClient();

  const [subject, setSubject] = useState('');
  const [meetingType, setMeetingType] = useState('call');
  const [startsAt, setStartsAt] = useState(() => toDateTimeLocal(new Date()));
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
  );
  const [location, setLocation] = useState('');
  const [isVirtual, setIsVirtual] = useState(true);
  const [linkedOpportunity, setLinkedOpportunity] = useState(opportunityId ?? '');
  const [agenda, setAgenda] = useState('');
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState('');
  const [sentiment, setSentiment] = useState('unknown');

  const [internal, setInternal] = useState<string[]>([]);
  const [customerContacts, setCustomerContacts] = useState<string[]>([]);
  const [freeTextAttendee, setFreeTextAttendee] = useState('');
  const [freeText, setFreeText] = useState<string[]>([]);
  const [nextSteps, setNextSteps] = useState<NextStep[]>([]);
  const [busy, setBusy] = useState(false);

  const contacts = useApiQuery<Paged<Row>>(
    ['contacts', accountId],
    `/api/contacts?accountId=${accountId}&pageSize=200`,
  );
  const opportunities = useApiQuery<Paged<Row>>(
    ['opportunities', 'for-account', accountId],
    // `exact`: a meeting on a parent offers the parent's own deals, not its
    // sites' (session 6) — a site's deal is logged on the site.
    `/api/opportunities?accountId=${accountId}&accountScope=exact&pageSize=100`,
  );

  /**
   * One request: the meeting, its attendees and its next steps are written in
   * a single transaction by `POST /api/meetings/log`. This used to be one
   * request per row, so a failure on a later row showed "not saved" over a
   * meeting that existed, and a retry duplicated it (docs/bugs.md 2026-10-10,
   * session 1 part 2). Now an error means nothing was written, and retrying
   * is safe.
   *
   * Edit mode (`meetingId`) is still a stub that creates a new meeting; it is
   * recorded as its own open entry and deliberately not touched here.
   */
  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const starts = fromDateTimeLocal(startsAt);
      const ends = new Date(new Date(starts).getTime() + Number(durationMinutes) * 60_000);
      const body: LogMeetingInput = {
        meeting: {
          subject: subject.trim(),
          meeting_type: meetingType as LogMeetingInput['meeting']['meeting_type'],
          starts_at: starts,
          ends_at: ends.toISOString(),
          timezone,
          location: location.trim() === '' ? null : location.trim(),
          is_virtual: isVirtual,
          account_id: accountId,
          opportunity_id: linkedOpportunity === '' ? null : linkedOpportunity,
          agenda: agenda.trim() === '' ? null : agenda.trim(),
          notes: notes.trim() === '' ? null : notes.trim(),
          outcome: outcome.trim() === '' ? null : outcome.trim(),
          sentiment: sentiment as LogMeetingInput['meeting']['sentiment'],
        },
        attendees: [
          ...internal.map((userId) => ({ attendee_type: 'internal' as const, user_id: userId })),
          ...customerContacts.map((contactId) => ({
            attendee_type: 'customer' as const,
            contact_id: contactId,
          })),
          ...freeText.map((name) => ({
            attendee_type: 'customer' as const,
            free_text_name: name,
          })),
        ],
        action_items: nextSteps
          .filter((step) => step.title.trim() !== '')
          .map((step) => ({
            title: step.title.trim(),
            due_date: step.dueDate,
            account_id: accountId,
            ...(linkedOpportunity === '' ? {} : { opportunity_id: linkedOpportunity }),
            owner_type: step.ownerContactId === '' ? ('internal' as const) : ('customer' as const),
            ...(step.ownerContactId === ''
              ? step.ownerUserId === ''
                ? {}
                : { owner_user_id: step.ownerUserId }
              : { owner_contact_id: step.ownerContactId }),
          })),
      };

      await api('/api/meetings/log', { method: 'POST', body });

      // Everything a new meeting can change: the Meetings page, the deal's
      // timeline and Actions tab, the account 360, and the action lists.
      for (const key of [['meetings'], ['opportunity'], ['account-summary'], ACTIONS_QUERY_KEY]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }

      push({ tone: 'success', title: 'Meeting logged' });
      onSaved();
    } catch (error) {
      // The server answered with its own error, so the transaction rolled
      // back. Only a gateway timeout or a dropped connection leaves the outcome
      // unknown, and then the message must not claim either way.
      const answered = error instanceof ApiError && ![502, 503, 504].includes(error.status);
      push({
        tone: 'error',
        title: error instanceof ApiError ? error.message : 'The connection failed.',
        body: answered
          ? 'Nothing was saved, so it is safe to try again.'
          : 'The meeting may or may not have been saved. Check the Activity tab before trying again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const customerAttendeeCount = customerContacts.length + freeText.length;

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={meetingId === undefined ? 'Log a meeting' : 'Edit meeting'}
      description="Attendees decide whether this counts as customer contact."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={busy || subject.trim() === ''}>
            {busy ? 'Saving…' : 'Save meeting'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject" className="col-span-2">
            <Input value={subject} onChange={(event) => setSubject(event.target.value)} autoFocus />
          </Field>
          <Field label="Type">
            <Select value={meetingType} onChange={(event) => setMeetingType(event.target.value)}>
              {MEETING_TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Starts">
            <Input
              type="datetime-local"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
            />
          </Field>
          <Field label="Duration (minutes)">
            <Input
              inputMode="numeric"
              value={durationMinutes}
              onChange={(event) => setDurationMinutes(event.target.value)}
            />
          </Field>
          <Field label="Timezone">
            <Input value={timezone} onChange={(event) => setTimezone(event.target.value)} />
          </Field>
          <Field label="Location">
            <Input
              value={location}
              placeholder={isVirtual ? 'Video link or platform' : 'Where'}
              onChange={(event) => setLocation(event.target.value)}
            />
          </Field>
          <Field label="Virtual?">
            <Select
              value={isVirtual ? 'yes' : 'no'}
              onChange={(event) => setIsVirtual(event.target.value === 'yes')}
            >
              <option value="yes">Virtual</option>
              <option value="no">In person</option>
            </Select>
          </Field>
          <Field label="Opportunity (optional)" className="col-span-2">
            <Select
              value={linkedOpportunity}
              onChange={(event) => setLinkedOpportunity(event.target.value)}
            >
              <option value="">Account-level meeting</option>
              {(opportunities.data?.items ?? []).map((opportunity) => (
                <option key={String(opportunity['id'])} value={String(opportunity['id'])}>
                  {String(opportunity['opp_number'])} — {String(opportunity['name'])}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <fieldset className="rounded-md border p-3">
          <legend className="px-1 text-xs font-medium">Internal attendees</legend>
          <div className="flex flex-wrap gap-2">
            {reference.users.map((user) => (
              <label key={user.id} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={internal.includes(user.id)}
                  onChange={(event) =>
                    setInternal((current) =>
                      event.target.checked
                        ? [...current, user.id]
                        : current.filter((id) => id !== user.id),
                    )
                  }
                />
                {user.display_name}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="rounded-md border p-3">
          <legend className="px-1 text-xs font-medium">
            Customer attendees{' '}
            {customerAttendeeCount === 0 && <Badge variant="risk">none yet</Badge>}
          </legend>
          <div className="flex flex-wrap gap-2">
            {(contacts.data?.items ?? []).map((contact) => {
              const id = String(contact['id']);
              return (
                <label key={id} className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={customerContacts.includes(id)}
                    onChange={(event) =>
                      setCustomerContacts((current) =>
                        event.target.checked
                          ? [...current, id]
                          : current.filter((existing) => existing !== id),
                      )
                    }
                  />
                  {String(contact['first_name'])} {String(contact['last_name'])}
                </label>
              );
            })}
          </div>

          <div className="mt-3 flex items-end gap-2">
            <Field label="Someone not on file" className="flex-1">
              <Input
                value={freeTextAttendee}
                placeholder="Name"
                onChange={(event) => setFreeTextAttendee(event.target.value)}
              />
            </Field>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (freeTextAttendee.trim() === '') return;
                setFreeText((current) => [...current, freeTextAttendee.trim()]);
                setFreeTextAttendee('');
              }}
            >
              Add
            </Button>
          </div>
          {freeText.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {freeText.join(', ')} — worth turning into contacts on the account so their role and
              disposition are recorded.
            </p>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">
            A meeting with no customer attendee does not count as customer contact, and will not
            clear the &ldquo;no contact in 30 days&rdquo; signal. Partners do not count either.
          </p>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Agenda (before)">
            <Textarea value={agenda} onChange={(event) => setAgenda(event.target.value)} rows={3} />
          </Field>
          <Field label="Notes (after)">
            <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
          </Field>
          <Field label="Outcome">
            <Input value={outcome} onChange={(event) => setOutcome(event.target.value)} />
          </Field>
          <Field label="Sentiment">
            <Select value={sentiment} onChange={(event) => setSentiment(event.target.value)}>
              {SENTIMENTS.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <fieldset className="rounded-md border p-3">
          <legend className="px-1 text-xs font-medium">Next steps</legend>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Each row becomes an action item, linked to this meeting and the deal.
          </p>
          <ul className="space-y-2">
            {nextSteps.map((step, index) => (
              <li key={index} className="flex items-end gap-2">
                <Field label="What" className="flex-1">
                  <Input
                    value={step.title}
                    onChange={(event) =>
                      setNextSteps((current) =>
                        current.map((existing, position) =>
                          position === index
                            ? { ...existing, title: event.target.value }
                            : existing,
                        ),
                      )
                    }
                  />
                </Field>
                <Field label="Owner">
                  <Select
                    value={
                      step.ownerContactId !== ''
                        ? `c:${step.ownerContactId}`
                        : `u:${step.ownerUserId}`
                    }
                    onChange={(event) => {
                      const [kind, value] = event.target.value.split(':');
                      setNextSteps((current) =>
                        current.map((existing, position) =>
                          position === index
                            ? {
                                ...existing,
                                ownerUserId: kind === 'u' ? (value ?? '') : '',
                                ownerContactId: kind === 'c' ? (value ?? '') : '',
                              }
                            : existing,
                        ),
                      );
                    }}
                  >
                    <option value="u:">Me</option>
                    {reference.users.map((user) => (
                      <option key={user.id} value={`u:${user.id}`}>
                        {user.display_name}
                      </option>
                    ))}
                    {(contacts.data?.items ?? []).map((contact) => (
                      <option key={String(contact['id'])} value={`c:${String(contact['id'])}`}>
                        {String(contact['first_name'])} {String(contact['last_name'])} (customer)
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Due">
                  <Input
                    type="date"
                    value={step.dueDate}
                    onChange={(event) =>
                      setNextSteps((current) =>
                        current.map((existing, position) =>
                          position === index
                            ? { ...existing, dueDate: event.target.value }
                            : existing,
                        ),
                      )
                    }
                  />
                </Field>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setNextSteps((current) =>
                      current.filter((_step, position) => position !== index),
                    )
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only">Remove</span>
                </Button>
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() =>
              setNextSteps((current) => [
                ...current,
                { title: '', ownerUserId: '', ownerContactId: '', dueDate: addDays(today(), 10) },
              ])
            }
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add next step
          </Button>
        </fieldset>
      </div>
    </Dialog>
  );
}
